import { CARD_TYPES, FRAME_STYLES, FRAME_WEIGHTS, GRID_SIZE } from "../constants/editor";

export type PixelColor = string | null;
export type PixelGrid = PixelColor[][];
export type ToolId = "draw" | "ink" | "fill" | "erase";
export type CardType = (typeof CARD_TYPES)[number];
export type FrameStyleId = (typeof FRAME_STYLES)[number]["id"];
export type FrameWeightId = (typeof FRAME_WEIGHTS)[number]["id"];
export type SceneLayerSlot = "background" | "character" | "effect";

export type SceneLayer = {
  slot: SceneLayerSlot;
  spriteId: string | null;
  offsetX: number;
  offsetY: number;
  scale: number;
  visible: boolean;
};

export type CardDraft = {
  name: string;
  description: string;
  hp: number;
  typeLabel: CardType["label"];
  frameStyle: FrameStyleId;
  frameWeight: FrameWeightId;
  frameColorManual: string | null;
  sceneLayers: SceneLayer[];
  grid: PixelGrid;
};

export type SubmissionStatus = "editing" | "submitted" | "locked";

export type CardSubmission = {
  round: number;
  status: SubmissionStatus;
  submittedAt: string | null;
  lockedAt: string | null;
  card: CardDraft;
};

export type GamePhase = "lobby" | "countdown" | "drafting" | "locked" | "reveal" | "battle" | "results" | "completed";

export type PlayerPresence = {
  id: string;
  name: string;
  connected: boolean;
  isReady: boolean;
  eliminated: boolean;
  score: number;
  joinedAt: string;
  updatedAt: string;
  card: CardDraft;
  submission: CardSubmission | null;
};

export type RoundState = {
  round: number;
  mode: "timed" | "solo";
  durationMs: number;
  phaseStartedAt: string;
  phaseEndsAt: string | null;
  draftingEndsAt: string | null;
  revealStartedAt: string | null;
  battleStartedAt: string | null;
  resultsStartedAt: string | null;
  battle: BattleResolution | null;
};

export type MatchSummary = {
  winnerId: string | null;
  winnerName: string | null;
  reason: string;
  completedAt: string;
};

export type BattleStats = {
  attack: number;
  defense: number;
  speed: number;
  creativity: number;
};

export type BattleRoundEntry = {
  playerId: string;
  playerName: string;
  cardName: string;
  typeLabel: string;
  hpBefore: number;
  hpAfter: number;
  damageDealt: number;
  damageTaken: number;
  scoreDelta: number;
  eliminated: boolean;
  stats: BattleStats;
};

export type BattleTurn = {
  attackerId: string;
  attackerName: string;
  attackerCardName: string;
  targetId: string;
  targetName: string;
  targetCardName: string;
  damage: number;
  critical: boolean;
  targetHpBefore: number;
  targetHpAfter: number;
  narration: string;
};

export type BattleResolution = {
  round: number;
  summary: string;
  entries: BattleRoundEntry[];
  turns: BattleTurn[];
};

export type RoomSnapshot = {
  id: string;
  phase: GamePhase;
  hostId: string;
  players: PlayerPresence[];
  round: RoundState;
  match: MatchSummary | null;
  createdAt: string;
  updatedAt: string;
};

export type ClientEvent =
  | { type: "hello"; playerId: string; playerName: string }
  | { type: "update_card"; card: CardDraft }
  | { type: "set_ready"; isReady: boolean }
  | { type: "set_round_duration"; durationMs: number }
  | { type: "set_round_mode"; mode: "timed" | "solo" }
  | { type: "start_game" }
  | { type: "submit_card" }
  | { type: "lock_submission" }
  | { type: "advance_phase" }
  | { type: "restart_match" }
  | { type: "ping"; now: string };

export type ServerEvent =
  | { type: "connected"; playerId: string; room: RoomSnapshot }
  | { type: "room_state"; room: RoomSnapshot }
  | { type: "error"; message: string };

export type JoinRoomResponse = {
  roomId: string;
  playerId: string;
  websocketPath: string;
  snapshot: RoomSnapshot;
};

function normalizeSceneScale(scale: number): number {
  const steps = [0.5, 1, 2, 3, 4];
  return steps.reduce((closest, candidate) => (
    Math.abs(candidate - scale) < Math.abs(closest - scale) ? candidate : closest
  ), 1);
}

export function createEmptyGrid(): PixelGrid {
  return Array.from({ length: GRID_SIZE }, () => Array.from({ length: GRID_SIZE }, () => null));
}

export function createDefaultCardDraft(): CardDraft {
  return {
    name: "My Monster",
    description: "",
    hp: 60,
    typeLabel: CARD_TYPES[0].label,
    frameStyle: "plain",
    frameWeight: "medium",
    frameColorManual: null,
    sceneLayers: createDefaultSceneLayers(),
    grid: createEmptyGrid(),
  };
}

export function cloneGrid(grid: PixelGrid): PixelGrid {
  return grid.map((row) => [...row]);
}

export function createDefaultSceneLayers(): SceneLayer[] {
  return [
    { slot: "background", spriteId: null, offsetX: 0, offsetY: 0, scale: 1, visible: true },
    { slot: "character", spriteId: null, offsetX: 0, offsetY: 0, scale: 1, visible: true },
    { slot: "effect", spriteId: null, offsetX: 0, offsetY: 0, scale: 1, visible: true },
  ];
}

export function cloneSceneLayers(sceneLayers?: SceneLayer[] | null): SceneLayer[] {
  const defaults = createDefaultSceneLayers();
  const bySlot = new Map((sceneLayers ?? []).map((layer) => [layer.slot, layer]));
  return defaults.map((fallback) => {
    const layer = bySlot.get(fallback.slot);
    return {
      slot: fallback.slot,
      spriteId: layer?.spriteId ?? null,
      offsetX: Number.isFinite(layer?.offsetX) ? Math.round(layer!.offsetX) : fallback.offsetX,
      offsetY: Number.isFinite(layer?.offsetY) ? Math.round(layer!.offsetY) : fallback.offsetY,
      scale: Number.isFinite(layer?.scale) ? normalizeSceneScale(layer!.scale) : fallback.scale,
      visible: layer?.visible ?? fallback.visible,
    };
  });
}

export function normalizeCardDraft(card?: Partial<CardDraft> | null): CardDraft {
  const fallback = createDefaultCardDraft();
  if (!card) {
    return fallback;
  }

  return {
    name: typeof card.name === "string" ? card.name : fallback.name,
    description: typeof card.description === "string" ? card.description : fallback.description,
    hp: typeof card.hp === "number" ? card.hp : fallback.hp,
    typeLabel: card.typeLabel ?? fallback.typeLabel,
    frameStyle: card.frameStyle ?? fallback.frameStyle,
    frameWeight: card.frameWeight ?? fallback.frameWeight,
    frameColorManual: card.frameColorManual ?? fallback.frameColorManual,
    sceneLayers: cloneSceneLayers(card.sceneLayers),
    grid: card.grid ? cloneGrid(card.grid) : fallback.grid,
  };
}

export function createEmptySubmission(round: number): CardSubmission {
  return {
    round,
    status: "editing",
    submittedAt: null,
    lockedAt: null,
    card: createDefaultCardDraft(),
  };
}

export function createInitialRound(now: string): RoundState {
  return {
    round: 1,
    mode: "timed",
    durationMs: 120_000,
    phaseStartedAt: now,
    phaseEndsAt: null,
    draftingEndsAt: null,
    revealStartedAt: null,
    battleStartedAt: null,
    resultsStartedAt: null,
    battle: null,
  };
}

export function createInitialRoomSnapshot(id: string, now: string): RoomSnapshot {
  return {
    id,
    phase: "lobby",
    hostId: "",
    players: [],
    round: createInitialRound(now),
    match: null,
    createdAt: now,
    updatedAt: now,
  };
}
